import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError, setUnauthorizedHandler } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = not auth, obj = user
    const [agencyId, setAgencyId] = useState(
        () => localStorage.getItem("agency_id") || null,
    );
    const [currentShift, setCurrentShift] = useState(null);

    const refreshShift = async (overrideAgencyId) => {
        const aid = overrideAgencyId || agencyId;
        if (!aid) {
            setCurrentShift(null);
            return null;
        }
        try {
            const { data } = await api.get(`/shifts/current?agency_id=${aid}`);
            setCurrentShift(data || null);
            return data || null;
        } catch (err) {
            console.warn("[auth] refreshShift failed:", err);
            setCurrentShift(null);
            return null;
        }
    };

    useEffect(() => {
        // Register a global 401 handler that clears session
        setUnauthorizedHandler(() => {
            localStorage.removeItem("token");
            localStorage.removeItem("agency_id");
            setAgencyId(null);
            setUser(false);
        });
        let alive = true;
        api.get("/auth/me")
            .then((r) => {
                if (!alive) return;
                setUser(r.data);
                if (r.data.agency_id && !agencyId) {
                    setAgencyId(r.data.agency_id);
                    localStorage.setItem("agency_id", r.data.agency_id);
                }
            })
            .catch(() => alive && setUser(false));
        return () => {
            alive = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("token", data.token);
            setUser(data.user);
            // Always refresh agency list after login: stored id may be stale
            try {
                const ags = await api.get("/agencies");
                if (ags.data.length > 0) {
                    const storedId = localStorage.getItem("agency_id");
                    const valid = ags.data.some((a) => a.id === storedId);
                    const pickId = valid ? storedId : (data.user.agency_id || ags.data[0].id);
                    setAgencyId(pickId);
                    localStorage.setItem("agency_id", pickId);
                }
            } catch (err) { console.warn("[auth] Failed to refresh agencies after login:", err); }
            return { ok: true };
        } catch (e) {
            return {
                ok: false,
                error: formatApiError(e.response?.data?.detail) || e.message,
            };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            localStorage.setItem("token", data.token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return {
                ok: false,
                error: formatApiError(e.response?.data?.detail) || e.message,
            };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) { console.warn("[auth] Logout API call failed (clearing local session anyway):", err); }
        localStorage.removeItem("token");
        localStorage.removeItem("agency_id");
        setAgencyId(null);
        setUser(false);
    };

    const selectAgency = (id) => {
        setAgencyId(id);
        localStorage.setItem("agency_id", id);
    };

    useEffect(() => {
        if (user && agencyId) {
            refreshShift(agencyId);
        } else {
            setCurrentShift(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, agencyId]);

    const value = useMemo(
        () => ({ user, setUser, login, register, logout, agencyId, selectAgency, currentShift, refreshShift }),
        [user, agencyId, currentShift],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
